import { useState } from "react";
import { array, shape, string, object, func } from "prop-types";
import cx from "classnames";

import Icon from "../Icon/Icon.jsx";
import Modal from "../../../../Common/js/components/Modal/Modal.jsx";
import PartnerModal from "../PartnerModal/PartnerModal.jsx";

const propTypes = {
  comparePageURL: string,
  homeWorldmapData: array.isRequired,
  dictionaryData: object.isRequired,
  onClose: func.isRequired,
  selectedState: shape({
    selectedTopic: string,
  }),
};

const CountryInfo = (props) => {
  const {
    comparePageURL,
    homeWorldmapData,
    dictionaryData,
    onClose,
    selectedState,
  } = props;

  const [partnerModalOpen, updatePartnerModalOpen] = useState(false);
  const [partnerStudy, setPartnerStudy] = useState(null);

  if (selectedState === null) {
    return null;
  }

  const noStateData = {
    case_studies: [],
    wip_case_studies: [],
    partner_content: [],
    non_partner_content: [],
  };

  const stateData =
    homeWorldmapData.find(
      (state) => state.code === selectedState.properties.iso_a3,
    ) || noStateData;

  const countryName = selectedState.name
    .toLowerCase()
    .replace(/\_/g, " ")
    .replace(/\w\S*/g, function (txt) {
      return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
    });

  const partnerStudies =
    selectedState.topicSelected !== dictionaryData.defaultTopicLabel
      ? stateData.partner_content.filter((e) => {
          return e.name === selectedState.topicSelected;
        })
      : stateData.partner_content;

  const nonPartnerStudies =
    selectedState.topicSelected !== dictionaryData.defaultTopicLabel
      ? stateData.non_partner_content.filter((e) => {
          return e.name === selectedState.topicSelected;
        })
      : stateData.non_partner_content;

  const caseStudiesExist = stateData.case_studies.length > 0;
  const wipStudiesExist = stateData.wip_case_studies.length > 0;
  const partnersExist = partnerStudies.length > 0;
  const nonPartnersExist = nonPartnerStudies.length > 0;
  const partnerOrExternalExist = partnersExist || nonPartnersExist;

  const noData =
    !caseStudiesExist && !wipStudiesExist && !partnerOrExternalExist;
  const partnersData = !caseStudiesExist && !wipStudiesExist && partnersExist;
  const externalsData =
    !caseStudiesExist && !wipStudiesExist && nonPartnersExist;

  const caseStudy = stateData.case_studies.filter((e) => {
    return e.name === selectedState.topicSelected;
  });

  const selectedTopicExemplar =
    selectedState.topicSelected !== dictionaryData.defaultTopicLabel &&
    caseStudy.length > 0;

  const showCases =
    caseStudy.length === 0 && !noData && !partnersData && !externalsData;

  const handleOpenPartnerModal = (study) => {
    updatePartnerModalOpen(true);
    setPartnerStudy(study);
  };

  const renderPartnerData = () => {
    return partnerStudies.map((study) => {
      return (
        <div className="country-info__external-content" key={study.title}>
          <div className="country-info__section-title">
            {dictionaryData.partnerContent}
          </div>
          <div className="country-info__case-title">{study.title}</div>
          <button
            type="button"
            className="btn btn--tertiary-ghost country-info__case-link country-info__case-link--partner"
            onClick={() => {
              handleOpenPartnerModal(study);
            }}
            aria-label="Open partner modal"
          >
            {dictionaryData.viewCaseStudy}
            <span className="btn__icon">
              <Icon iconName="arrow-right" />
            </span>
          </button>
        </div>
      );
    });
  };

  const renderNonPartnerData = () => {
    return nonPartnerStudies.map((study) => {
      return (
        <div className="country-info__external-content" key={study.title}>
          <div className="country-info__section-title">
            {dictionaryData.externalContent}
          </div>
          <div className="country-info__case-title">{study.title}</div>
          <a
            href={study.link}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn--tertiary-ghost country-info__case-link country-info__case-link--non-partner"
            aria-label={`${dictionaryData.viewCaseStudy} - ${study.title}`}
          >
            {dictionaryData.viewCaseStudy}
            <span className="btn__icon">
              <Icon iconName="arrow-right" />
            </span>
          </a>
        </div>
      );
    });
  };

  const classes = cx("country-info__name", {
    "color-lightblue": caseStudiesExist || partnerOrExternalExist,
    "color-turquoise": !caseStudiesExist && !partnerOrExternalExist,
  });

  return (
    <div className="country-info">
      <button
        type="button"
        id="return-to-map"
        className="country-info__back"
        onClick={onClose}
        data-label="return_to_map"
      >
        <div className="country-info__back-link">
          <div className="country-info__back-icon">
            <img
              src="/static/files/arrow_icon-orange-return_to_map.svg"
              alt=""
            />
          </div>
          <div className="country-info__back-text">
            <span>{dictionaryData.returnToMap}</span>
          </div>
        </div>
      </button>

      {/* If a topic is selected and this country is an exemplar of it */}
      {selectedTopicExemplar && (
        <div id="topic-homeworldmap" className="country-info__topic-name">
          {selectedState.topicSelected}
        </div>
      )}

      <div className={classes}>{selectedState.name}</div>

      {/* If a topic is selected and this country is an exemplar of it */}
      {selectedTopicExemplar &&
        caseStudy.map((caseData) => {
          return (
            <>
              {caseData.chart !== "" && (
                <div className="country-info__chart">
                  <img src={caseData.chart} alt="" />
                </div>
              )}
              <div className="country-info__section-title country-info__section-title--light">
                {caseData.title}
              </div>
              <div className="country-info__case-message">
                {caseData.description}
              </div>
              {caseData.url && (
                <a
                  href={caseData.url}
                  className="btn btn--tertiary country-info__case-link home-worldmap-button"
                >
                  {dictionaryData.viewNarrative}
                  <span className="btn__icon">
                    <Icon iconName="arrow-right" />
                  </span>
                </a>
              )}
            </>
          );
        })}

      {/* Otherwise, display other content for this country */}
      {showCases && (
        <>
          <div className="country-info__section-title">
            {dictionaryData.exemplarNarratives || "EXEMPLAR NARRATIVES"}
          </div>
          <table className="country-info__cases-table">
            <tbody>
              {stateData.case_studies.map((study) => {
                return (
                  <tr
                    onClick={() => {
                      window.open(`${study.url}`, "_self");
                    }}
                    className="country-info-hover"
                    data-label="case study"
                    key={study.name}
                  >
                    <td width="55px" className="country-info__case-icon">
                      {study.icon && study.icon.length > 0 && (
                        <img src={study.icon} alt="" />
                      )}
                    </td>
                    <td width="75%" className="country-info__case-type">
                      <span>{study.name}</span>
                      <Icon iconName="arrow-right" />
                    </td>
                  </tr>
                );
              })}
              {stateData.wip_case_studies.map((study) => {
                return (
                  <tr
                    key={study.name}
                    className="country-info__case-row"
                    aria-label="Show Newsletter Modal"
                    onClick={() => window.openGlobalModal("newsletter")}
                  >
                    <td width="55px" className="country-info__case-icon">
                      {study.icon && study.icon.length > 0 && (
                        <img src={study.icon} alt="" />
                      )}
                    </td>
                    <td
                      className="country-info__case-type country-info__case-type--wip"
                      width="75%"
                    >
                      <span className="color-turquoise-important">
                        {study.name}
                      </span>
                      <br />
                      <small>
                        {dictionaryData.comingSoon}{" "}
                        <em>{dictionaryData.getNotified}</em>
                      </small>
                      <Icon iconName="arrow-right" />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="country-info__cases-external">
            {partnersExist && renderPartnerData()}
            {nonPartnersExist && renderNonPartnerData()}
          </div>
        </>
      )}
      {partnersData && renderPartnerData()}
      {externalsData && renderNonPartnerData()}

      {/* If there's no data at all for this country */}
      {noData && (
        <>
          <span className="country-info__section-title">
            {dictionaryData.genericSubtitle}
          </span>
          <div className="country-info__case-message">
            {(dictionaryData.genericDescription || "").replace(
              /\[\[COUNTRY_NAME\]\]/g,
              countryName,
            )}
          </div>
          <a
            className="btn btn--tertiary country-info__case-link home-worldmap-button"
            href={`${comparePageURL || "/compare"}/?country=${stateData.code}`}
          >
            {dictionaryData.compare}
            <span className="btn__icon">
              <Icon iconName="arrow-right" />
            </span>
          </a>
        </>
      )}

      <Modal
        className="modal--partners"
        noPadding
        isOpen={partnerModalOpen}
        onClose={() => {
          updatePartnerModalOpen(false);
        }}
        title={false}
      >
        {partnerStudy && (
          <PartnerModal dictionaryData={dictionaryData} {...partnerStudy} />
        )}
      </Modal>
    </div>
  );
};

CountryInfo.propTypes = propTypes;
export default CountryInfo;
